import { Fragment, useState } from 'react';
import type { PromptResult } from '@visiward/schemas';
import { PROMPT_CATEGORY_LABELS } from '@visiward/shared';
import { Badge } from '@/components/ui/misc';
import { Check, Minus, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

function sentimentTone(s: PromptResult['sentiment']) {
  return s === 'positive' ? 'teal' : s === 'negative' ? 'danger' : 'default';
}

export function PromptResultsTable({ results }: { results: PromptResult[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
            <th className="px-4 py-3 font-medium">Prompt</th>
            <th className="px-3 py-3 font-medium">Category</th>
            <th className="px-3 py-3 text-center font-medium">Mentioned</th>
            <th className="px-3 py-3 text-center font-medium">Recommended</th>
            <th className="px-3 py-3 text-center font-medium">Rank</th>
            <th className="px-3 py-3 font-medium">Sentiment</th>
            <th className="px-3 py-3" />
          </tr>
        </thead>
        <tbody>
          {results.map((r, i) => (
            <Fragment key={i}>
              <tr
                className={cn(
                  'border-b border-border/60 transition hover:bg-bg-raised/40',
                  expanded === i && 'bg-bg-raised/40',
                )}
              >
                <td className="max-w-xs px-4 py-3">
                  <p className="truncate text-text-primary" title={r.promptText}>
                    {r.promptText}
                  </p>
                </td>
                <td className="px-3 py-3 text-text-secondary">
                  {PROMPT_CATEGORY_LABELS[r.category]}
                </td>
                <td className="px-3 py-3 text-center">
                  {r.brandMentioned ? (
                    <Check size={16} className="mx-auto text-accent-teal" />
                  ) : (
                    <Minus size={16} className="mx-auto text-text-muted" />
                  )}
                </td>
                <td className="px-3 py-3 text-center">
                  {r.recommended ? (
                    <Check size={16} className="mx-auto text-accent-teal" />
                  ) : (
                    <Minus size={16} className="mx-auto text-text-muted" />
                  )}
                </td>
                <td className="px-3 py-3 text-center text-text-primary">
                  {r.rankPosition ?? '—'}
                </td>
                <td className="px-3 py-3">
                  <Badge tone={sentimentTone(r.sentiment)}>{r.sentiment}</Badge>
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="text-text-muted transition hover:text-text-primary"
                    aria-label="Toggle details"
                  >
                    <ChevronDown
                      size={16}
                      className={cn('transition', expanded === i && 'rotate-180')}
                    />
                  </button>
                </td>
              </tr>
              {expanded === i && (
                <tr className="border-b border-border/60 bg-bg/40">
                  <td colSpan={7} className="px-4 py-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                          Answer excerpt
                        </p>
                        <p className="text-sm text-text-secondary">
                          {r.responseExcerpt || 'No excerpt available.'}
                        </p>
                        {r.competitors.length > 0 && (
                          <div className="mt-3">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Competitors named
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {r.competitors.map((cName) => (
                                <Badge key={cName} tone="violet">
                                  {cName}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        {r.issues.length > 0 && (
                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Issues
                            </p>
                            <ul className="list-inside list-disc text-sm text-text-secondary">
                              {r.issues.map((issue, k) => (
                                <li key={k}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {r.recommendations.length > 0 && (
                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                              Suggestions
                            </p>
                            <ul className="list-inside list-disc text-sm text-text-secondary">
                              {r.recommendations.map((rec, k) => (
                                <li key={k}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="flex gap-4 text-xs text-text-muted">
                          <span>Accuracy: {Math.round(r.accuracyScore)}</span>
                          <span>Alignment: {Math.round(r.messageAlignment)}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
